import { FunctionComponent, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  setDeleteLeadsStatus,
  showSnackbarAction
} from "../../../Redux/actions";
import { deleteRecords } from "../../../Services/contacts";
import axios from "axios";
import { url } from "../../../Values/constants";
import { themeColors } from "../../theme";
import { deleteNews } from "../../../Services/organizations";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import { Circles } from "react-loader-spinner";

/* ------------------------------------------------------------------
   COMPONENT PROPS
------------------------------------------------------------------ */
type Props = {
  open: boolean;
  close: () => void;
  data: any[];
  clearSelectedRowsData?: () => void;
  setOwner?: any;
  theme: any;
  tableType?: string;
  newsData?: any[];
  user: any;
  organizationId: any;
  history?: any;
  authToken: string;
};

/* ------------------------------------------------------------------
   DELETE SELECTED MODAL
------------------------------------------------------------------ */
const DeleteSelectedModal: FunctionComponent<Props> = ({
  open,
  close,
  clearSelectedRowsData,
  setOwner,
  data,
  theme,
  newsData = [],
  tableType,
  organizationId,
  authToken
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  const [load, setLoad] = useState(false);
  const dispatcher = useDispatch();

  /* ------------------------------------------------------------------
     MAIN DELETE HANDLER (OPTIMIZED)
  ------------------------------------------------------------------ */
  const deleteSelectedRecord = async () => {
    try {
      setLoad(true);

      /* ------------------ CASE 1 : NEWS ------------------ */
      if (tableType === "NEWS") {
        const filtered = newsData.filter(
          (el: any) =>
            el.createNewsProfile !== "Super Admin" &&
            !data.some((f: any) => f.name === el.name)
        );

        deleteNews(organizationId, filtered, dispatcher);
        setOwner(false);
        close();
        return;
      }

      /* ------------------ CASE 2 : NEWS SUPER ADMIN ------------------ */
      if (tableType === "NEWS SUPER ADMIN") {
        const filtered = newsData.filter(
          (el: any) => !data.some((f: any) => f.name === el.name)
        );

        deleteNews(" ", filtered, dispatcher);
        setOwner(false);
        close();
        return;
      }

      /* ------------------ CASE 3 : LEADS ------------------ */
      const leadIds = data.map((d: any) => d.contactId);

      if (leadIds.length > 200) {
        dispatcher(
          showSnackbarAction("Maximum 200 records allowed", "error")
        );
        return;
      }

      axios.defaults.headers.common["x-access-token"] = authToken;

      /* 1️⃣ Delete leads */
      await deleteRecords(data);

      /* 2️⃣ Delete call logs + tasks in PARALLEL */
      const deleteRequests = data.flatMap((item: any) => [
        axios.delete(url + "/callLogs/deleteCallLogs", {
          data: { leadId: item.contactId }
        }),
        axios.delete(url + "/tasks/deleteTask", {
          data: { leadId: item.contactId }
        })
      ]);

      await Promise.all(deleteRequests);

      /* UI Updates */
      clearSelectedRowsData?.();
      dispatcher(setDeleteLeadsStatus(true));
      dispatcher(showSnackbarAction("Record Deleted", "success"));
      close();
    } catch (error) {
      console.error("Delete failed:", error);
      dispatcher(showSnackbarAction("Delete Failed", "error"));
    } finally {
      setLoad(false);
    }
  };

  /* ------------------------------------------------------------------
    MODAL STYLE CONFIGURATION
    Custom styling for react-modal overlay & content
    ------------------------------------------------------------------ */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /* ------------------------------------------------------------------
    RENDER MODAL
    ------------------------------------------------------------------ */
  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >

        <Box
          sx={{
            width: "100%",
            maxWidth: 340,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <form style={{ width: "100%" }}>
            {/* ---------------------------------------------- */}
            {/* Modal Title                                    */}
            {/* ---------------------------------------------- */}
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  letterSpacing: 0.5,
                }}
              >
                Are you sure you want to delete the selected records?
              </h3>
            </Box>

            {/* ---------------------------------------------- */}
            {/* Selected Items Preview                         */}
            {/* ---------------------------------------------- */}
            <Grid container spacing={2} sx={{ width: "100%", mt: 2, maxHeight: 200, overflowY: 'auto' }}>
              <ol>
                {data.map((dat: any) => (
                  <li>{dat.customer_name || dat.name}</li>
                ))}
              </ol>
            </Grid>

            {/* ---------------------------------------------- */}
            {/* Action Buttons                                 */}
            {/* ---------------------------------------------- */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  <StyledCancelButton
                    onClick={deleteSelectedRecord}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />Deleting...
                      </span>
                    ) : (
                      <>Delete</>
                    )}
                  </StyledCancelButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

/* ------------------------------------------------------------------
   REDUX → Map state to props
   ------------------------------------------------------------------ */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode,
    organizationId: state.organization.id,
    authToken: state.user.authToken
  };
};
export default connect(mapStateToProps)(DeleteSelectedModal);
